import { z } from 'zod'

/**
 * Rooms and bookings — the pure half.
 *
 * The *authority* on "is this room free?" is a Postgres exclusion constraint;
 * nothing here can be trusted to keep two bookings apart, because two browsers
 * cannot see each other. What lives here is the same arithmetic used to warn
 * somebody *before* they submit — the event editor drawing a red band across a
 * clashing slot — and to explain the refusal when the database says no.
 *
 * Keeping both on the same function matters: a warning that disagrees with the
 * constraint is worse than no warning, because people learn to trust it.
 */

export const BOOKING_STATUSES = ['pending', 'approved', 'rejected', 'released'] as const
export type BookingStatus = (typeof BOOKING_STATUSES)[number]

/** The two statuses that hold a room. Mirrors the constraint's WHERE clause. */
export const HOLDING_STATUSES: readonly BookingStatus[] = ['pending', 'approved']

export const isHolding = (status: BookingStatus): boolean => HOLDING_STATUSES.includes(status)

export interface TimeWindow {
  startsAt: Date
  endsAt: Date
}

/**
 * Half-open, `[start, end)`.
 *
 * A booking that ends at 10:00 does not overlap one that starts at 10:00 —
 * which is how people actually book rooms back to back, and what the `[)`
 * range in the constraint means.
 */
export function overlaps(a: TimeWindow, b: TimeWindow): boolean {
  return a.startsAt < b.endsAt && b.startsAt < a.endsAt
}

export interface ExistingBooking extends TimeWindow {
  id: string
  resourceId: string
  status: BookingStatus
  purpose: string
}

/**
 * What a proposed booking would clash with.
 *
 * Only bookings that hold the room count: a rejected request is a room that is
 * free, and showing it as a clash would have people queueing behind a booking
 * that no longer exists. `ignoreId` is for editing a booking, which must not
 * find itself.
 */
export function findConflicts(
  candidate: TimeWindow & { resourceId: string },
  existing: readonly ExistingBooking[],
  ignoreId?: string,
): ExistingBooking[] {
  return existing.filter(
    (booking) =>
      booking.resourceId === candidate.resourceId &&
      booking.id !== ignoreId &&
      isHolding(booking.status) &&
      overlaps(candidate, booking),
  )
}

/**
 * The sentence a person can act on.
 *
 * "That room is taken" is true and useless; what somebody needs is *by what*
 * and *until when*, so they can either move their own booking or go and ask
 * the group that has it.
 */
export function describeConflict(
  conflict: ExistingBooking,
  formatTime: (date: Date) => string = (date) => date.toISOString().slice(11, 16),
): string {
  const held = conflict.status === 'pending' ? 'requested' : 'booked'
  return `Already ${held} for ${conflict.purpose}, ${formatTime(conflict.startsAt)}–${formatTime(conflict.endsAt)}`
}

/**
 * Free gaps in a day, given what is already booked.
 *
 * The calendar uses this to offer "the hall is free 14:00–17:00" instead of
 * making somebody guess at times until one is accepted.
 */
export function freeWindows(
  day: TimeWindow,
  booked: readonly TimeWindow[],
  minimumMinutes = 30,
): TimeWindow[] {
  const held = [...booked]
    .filter((booking) => overlaps(day, booking))
    .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())

  const gaps: TimeWindow[] = []
  let cursor = day.startsAt

  for (const booking of held) {
    if (booking.startsAt > cursor) gaps.push({ startsAt: cursor, endsAt: booking.startsAt })
    // Bookings can overlap each other even when neither may overlap *this*
    // one — different resources, or history from before the constraint. Take
    // the furthest end so the cursor never walks backwards.
    if (booking.endsAt > cursor) cursor = booking.endsAt
  }
  if (cursor < day.endsAt) gaps.push({ startsAt: cursor, endsAt: day.endsAt })

  const floor = minimumMinutes * 60 * 1000
  return gaps.filter((gap) => gap.endsAt.getTime() - gap.startsAt.getTime() >= floor)
}

// ---- equipment, booked by quantity -----------------------------------------------

/** A booking of equipment, or a room's booking (no quantity: the whole thing). */
export interface QuantityBooking extends TimeWindow {
  quantity: number | null
}

/**
 * The most of a piece of equipment in use at any one moment of a window.
 *
 * Not the sum of everything that overlaps it: a booking 9–10 and another
 * 11–12 both overlap 9–12 but never each other, and twenty chairs at nine
 * plus twenty at eleven is twenty in use, not forty. A booking with no
 * quantity holds every one (`stock`).
 */
export function peakInUse(window: TimeWindow, bookings: readonly QuantityBooking[], stock: number) {
  const edges: { at: number; change: number }[] = []
  for (const booking of bookings) {
    if (!overlaps(window, booking)) continue
    const amount = booking.quantity ?? stock
    const from = Math.max(booking.startsAt.getTime(), window.startsAt.getTime())
    const to = Math.min(booking.endsAt.getTime(), window.endsAt.getTime())
    edges.push({ at: from, change: amount }, { at: to, change: -amount })
  }
  // Ends before starts at the same instant: [) windows that touch do not overlap.
  edges.sort((a, b) => a.at - b.at || a.change - b.change)
  let current = 0
  let peak = 0
  for (const edge of edges) {
    current += edge.change
    if (current > peak) peak = current
  }
  return peak
}

export type EquipmentFit =
  { ok: true; available: number } | { ok: false; available: number; reason: string }

/**
 * Whether `wanted` more fit alongside what is already booked for the window.
 * `stock` is how many exist (the resource's capacity; one when unset).
 */
export function fitsEquipment(
  candidate: TimeWindow & { quantity: number },
  existing: readonly QuantityBooking[],
  stock: number,
): EquipmentFit {
  const available = Math.max(0, stock - peakInUse(candidate, existing, stock))
  if (candidate.quantity <= available) return { ok: true, available }
  return {
    ok: false,
    available,
    reason:
      available === 0
        ? `All ${stock} are booked for that time.`
        : `Only ${available} of the ${stock} are free for that time.`,
  }
}

/** How many of a resource exist, for quantity booking. */
export const stockOf = (resource: { capacity: number | null }) => resource.capacity ?? 1

// ---- schemas ---------------------------------------------------------------

export const FACILITY_KINDS = ['room', 'equipment'] as const
export type FacilityKind = (typeof FACILITY_KINDS)[number]

export const createResourceSchema = z.object({
  name: z.string().trim().min(1, 'Name the room').max(120),
  kind: z.enum(FACILITY_KINDS).default('room'),
  campusId: z.uuid().nullish(),
  description: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  capacity: z.number().int().min(1).max(100_000).nullish(),
  isBookable: z.boolean().default(true),
  requiresApproval: z.boolean().default(false),
  color: z.string().trim().max(20).nullish(),
})
export type CreateResourceInput = z.infer<typeof createResourceSchema>
export type CreateResourceFormInput = z.input<typeof createResourceSchema>

export const updateResourceSchema = createResourceSchema.partial()
export type UpdateResourceInput = z.infer<typeof updateResourceSchema>

/** A year is longer than any real booking and shorter than a data-entry slip. */
const MAX_BOOKING_HOURS = 24 * 14

export const createBookingSchema = z
  .object({
    resourceId: z.uuid('Choose a room'),
    purpose: z.string().trim().min(1, 'Say what it is for').max(200),
    notes: z
      .string()
      .trim()
      .max(1000)
      .nullish()
      .transform((v) => (v === '' || v === undefined ? null : v)),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    eventInstanceId: z.uuid().nullish(),
    /** How many, for equipment ("30 chairs"). Ignored for a room, which is booked whole. */
    quantity: z.number().int().min(1, 'Book at least one').max(100_000).nullish(),
  })
  .refine((booking) => new Date(booking.endsAt) > new Date(booking.startsAt), {
    message: 'The end has to come after the start',
    path: ['endsAt'],
  })
  .refine(
    (booking) =>
      new Date(booking.endsAt).getTime() - new Date(booking.startsAt).getTime() <=
      MAX_BOOKING_HOURS * 60 * 60 * 1000,
    { message: 'That booking is longer than two weeks — check the dates', path: ['endsAt'] },
  )
export type CreateBookingInput = z.infer<typeof createBookingSchema>
export type CreateBookingFormInput = z.input<typeof createBookingSchema>

export const decideBookingSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
})
export type DecideBookingInput = z.infer<typeof decideBookingSchema>

// ---- what an event about to be approved would clash with ----------------------

/**
 * The same place, by name: "Main Hall" and " main  hall" are one room.
 * An empty place is nowhere in particular and clashes with nothing.
 */
export function samePlace(a: string | null, b: string | null): boolean {
  const norm = (text: string | null) => text?.trim().replace(/\s+/g, ' ').toLowerCase() ?? ''
  const left = norm(a)
  return left !== '' && left === norm(b)
}

/** Campuses clash when they are the same, or when either is church-wide. */
const sameCampus = (a: string | null, b: string | null) => a === null || b === null || a === b

/** One date of the event being approved, where it happens. */
export interface PlannedDate extends TimeWindow {
  instanceId: string
  location: string | null
  campusId: string | null
}

/** Another event's date already on the calendar. */
export interface OtherDate extends PlannedDate {
  eventId: string
  title: string
}

/** A room held for something else. */
export interface HeldRoom extends TimeWindow {
  bookingId: string
  resourceId: string
  resourceName: string
  campusId: string | null
  purpose: string
  status: BookingStatus
  eventInstanceId: string | null
}

export type EventClash =
  | { kind: 'event'; date: PlannedDate; other: OtherDate }
  | { kind: 'booking'; date: PlannedDate; booking: HeldRoom }

/**
 * Everything an event's dates clash with: another event in the same place at
 * the same time (same campus, or either church-wide), or a room of that name
 * held for something else. A booking held for one of the event's own dates is
 * not a clash — it is the event's room.
 *
 * Advice for the approver, not a refusal: the exclusion constraint is the
 * authority on rooms, and two events can share a hall on purpose.
 */
export function eventClashes(
  dates: readonly PlannedDate[],
  others: readonly OtherDate[],
  rooms: readonly HeldRoom[],
): EventClash[] {
  const own = new Set(dates.map((d) => d.instanceId))
  const clashes: EventClash[] = []
  for (const date of dates) {
    for (const other of others)
      if (
        !own.has(other.instanceId) &&
        overlaps(date, other) &&
        sameCampus(date.campusId, other.campusId) &&
        samePlace(date.location, other.location)
      )
        clashes.push({ kind: 'event', date, other })
    for (const booking of rooms)
      if (
        isHolding(booking.status) &&
        !(booking.eventInstanceId && own.has(booking.eventInstanceId)) &&
        overlaps(date, booking) &&
        sameCampus(date.campusId, booking.campusId) &&
        samePlace(date.location, booking.resourceName)
      )
        clashes.push({ kind: 'booking', date, booking })
  }
  return clashes
}
